import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { ORG_TYPES, EXPERTISE } from "../lib/constants";

export default function AanmeldenInlener() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({
    organization_name: "", contact_name: "", job_title: "", email: "", password: "",
    phone: "", organization_type: "", interest_areas: [], how_found_us: "", message: "",
    consent: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (a) => set("interest_areas", form.interest_areas.includes(a) ? form.interest_areas.filter((x) => x !== a) : [...form.interest_areas, a]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Geef je AVG-toestemming.");
    if (!form.organization_type) return toast.error("Kies type organisatie.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register-client", form);
      setUser(data);
      toast.success("Bedankt! We nemen spoedig contact op.");
      nav("/inlener-dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Aanmelding mislukt");
    }
    setBusy(false);
  };

  return (
    <section className="ok-section">
      <div className="ok-container grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="ok-eyebrow mb-3">Voor inleners</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
            Aanmelden als inlener.
          </h1>
          <p className="mt-5 text-[#4a5d5e] leading-relaxed">
            Vertel ons over uw organisatie en interessegebieden. Wij komen met passende kandidaten, direct of via Flextender.
          </p>
          <div className="mt-8 aspect-video overflow-hidden border border-[#e2e8f0]">
            <img src="https://images.pexels.com/photos/14911054/pexels-photo-14911054.jpeg" alt="Den Haag architectuur" className="w-full h-full object-cover" />
          </div>
          <p className="mt-6 text-sm">Heeft u al een account? <Link to="/login" className="text-[#01696f] font-semibold">Inloggen</Link></p>
        </div>

        <form onSubmit={submit} className="lg:col-span-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Organisatienaam" required>
              <input data-testid="cli-org" required className="ok-input" value={form.organization_name} onChange={(e) => set("organization_name", e.target.value)} />
            </F>
            <F label="Type organisatie" required>
              <select data-testid="cli-type" required className="ok-input" value={form.organization_type} onChange={(e) => set("organization_type", e.target.value)}>
                <option value="">Kies een optie</option>
                {ORG_TYPES.map((x) => <option key={x}>{x}</option>)}
              </select>
            </F>
            <F label="Contactpersoon" required>
              <input data-testid="cli-contact" required className="ok-input" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
            </F>
            <F label="Functietitel" required>
              <input data-testid="cli-job-title" required className="ok-input" value={form.job_title} onChange={(e) => set("job_title", e.target.value)} />
            </F>
            <F label="E-mailadres" required>
              <input data-testid="cli-email" required type="email" className="ok-input" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </F>
            <F label="Wachtwoord" required>
              <input data-testid="cli-password" required type="password" minLength={8} className="ok-input" value={form.password} onChange={(e) => set("password", e.target.value)} />
            </F>
            <F label="Telefoonnummer">
              <input data-testid="cli-phone" className="ok-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </F>
            <F label="Hoe vond u ons?">
              <input data-testid="cli-found" className="ok-input" value={form.how_found_us} onChange={(e) => set("how_found_us", e.target.value)} />
            </F>
          </div>

          <F label="Interessegebieden (meerdere mogelijk)">
            <div className="flex flex-wrap gap-2">
              {EXPERTISE.map((a) => {
                const active = form.interest_areas.includes(a);
                return (
                  <button key={a} type="button" data-testid={`cli-area-${a}`} onClick={() => toggle(a)}
                    className={`px-3 py-1.5 text-xs border rounded-sm ${active ? "bg-[#01696f] text-white border-[#01696f]" : "bg-white border-[#e2e8f0] hover:border-[#01696f]"}`}>
                    {a}
                  </button>
                );
              })}
            </div>
          </F>

          <F label="Bericht (optioneel)">
            <textarea data-testid="cli-message" rows={4} className="ok-input" value={form.message} onChange={(e) => set("message", e.target.value)} />
          </F>

          <label className="flex items-start gap-3 text-sm">
            <input data-testid="cli-consent" type="checkbox" className="mt-1" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} />
            <span>Ik ga akkoord met de verwerking van mijn gegevens conform de AVG.</span>
          </label>

          <button data-testid="cli-submit" disabled={busy} className="bg-[#01696f] text-white px-8 py-3 rounded-sm hover:bg-[#015257] disabled:opacity-60 font-medium">
            {busy ? "Bezig…" : "Aanmelden als inlener"}
          </button>
        </form>
      </div>

      <style>{`
        .ok-input { width: 100%; padding: 0.625rem 0.75rem; border: 1px solid #e2e8f0; background: #fff; font-size: 0.875rem; border-radius: 2px; }
        .ok-input:focus { outline: 2px solid #01696f; outline-offset: 1px; }
      `}</style>
    </section>
  );
}

function F({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-semibold text-[#4a5d5e] mb-2">
        {label}{required && <span className="text-[#d32f2f]"> *</span>}
      </label>
      {children}
    </div>
  );
}
